import React, { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { TextField, btnGhost, btnPrimary, inputCls } from '../ui.jsx';
import { ICON_LABELS, iconFor } from '../../../../components/producerPage/icons.js';

const MAX_CUSTOM = 5;

/** Step 3 — pick products to showcase, or add custom ones. */
export default function ProductsStep({ ctx }) {
  const { page, patch, catalog } = ctx;
  const [draft, setDraft] = useState(null); // custom-product form, or null when closed
  const toggle = (id) => patch({ products: page.products.includes(id) ? page.products.filter((p) => p !== id) : [...page.products, id] });

  function addCustom() {
    if (!draft?.title.trim()) return;
    patch({ customProducts: [...page.customProducts, { id: `c_${page.customProducts.length + 1}`, ...draft, title: draft.title.trim(), description: draft.description.trim() }] });
    setDraft(null);
  }

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-lg font-semibold">Products Offered</h3>
        <p className="text-xs text-white/60">Select the services you offer. They appear on your producer page. Choose at least one.</p>
      </div>

      <div className="grid gap-2 sm:grid-cols-2" role="group" aria-label="Products">
        {(catalog?.products || []).map((p) => {
          const on = page.products.includes(p.id);
          const Icon = iconFor(p.icon);
          return (
            <button
              key={p.id}
              type="button"
              aria-pressed={on}
              onClick={() => toggle(p.id)}
              data-testid={`pp-product-${p.id}`}
              className={`flex items-center gap-2 rounded-lg border px-3 py-2.5 text-left text-sm transition ${on ? 'border-civix-purple bg-civix-purple/20 text-white' : 'border-white/10 text-white/70 hover:bg-white/5'}`}
            >
              <Icon className="h-4 w-4 shrink-0" /> {p.title}
            </button>
          );
        })}
      </div>

      {page.customProducts.length > 0 && (
        <div className="space-y-2">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-white/50">Custom products</div>
          {page.customProducts.map((c, i) => (
            <div key={c.id} className="flex items-start justify-between gap-3 rounded-lg border border-white/10 p-3">
              <div><div className="text-sm font-semibold">{c.title}</div><div className="text-xs text-white/60">{c.description}</div></div>
              <button type="button" aria-label={`Remove ${c.title}`} onClick={() => patch({ customProducts: page.customProducts.filter((_, j) => j !== i) })} className="rounded p-1 text-white/50 hover:text-white"><X className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}

      {draft ? (
        <div className="space-y-3 rounded-lg border border-white/10 p-4" data-testid="pp-custom-form">
          <TextField label="Product title" value={draft.title} onChange={(v) => setDraft({ ...draft, title: v })} maxLength={60} required />
          <TextField label="Description" value={draft.description} onChange={(v) => setDraft({ ...draft, description: v })} multiline rows={3} maxLength={400} />
          <select className={inputCls} value={draft.icon} onChange={(e) => setDraft({ ...draft, icon: e.target.value })} aria-label="Icon">
            {Object.entries(ICON_LABELS).map(([k, l]) => <option key={k} value={k} className="text-black">{l}</option>)}
          </select>
          <div className="flex gap-2">
            <button type="button" className={btnPrimary} disabled={!draft.title.trim()} onClick={addCustom}>Add product</button>
            <button type="button" className={btnGhost} onClick={() => setDraft(null)}>Cancel</button>
          </div>
        </div>
      ) : (
        <button type="button" className={btnGhost} disabled={page.customProducts.length >= MAX_CUSTOM} onClick={() => setDraft({ title: '', description: '', icon: 'shield' })} data-testid="pp-create-custom">
          <Plus className="mr-1 inline h-3.5 w-3.5" />Create custom product
        </button>
      )}
    </div>
  );
}
